import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Info, Users } from 'lucide-react';
import { fetchCampagnesRequest } from '../../../redux/actions/campagnesActions';
import { fetchManagerInboxRequest, fetchSkillEvalsByUserRequest } from '../../../redux/actions/skillEvaluationActions';
import SkillGapTable from '../components/SkillGapTable';
import '../EvaluationDesign.css';

export default function GapPage() {
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.auth || {});
    const { list: campagnesList } = useSelector(state => state.campagnes || { list: [] });
    const { inboxManager, skillEvaluations } = useSelector(state => state.skillEvaluation || { inboxManager: [], skillEvaluations: [], loading: false });

    const [selectedCampagneId, setSelectedCampagneId] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    const isManagerOrRH = useMemo(() => {
        const role = (user?.role?.name || user?.role || "").toUpperCase();
        return role.includes("MANAGER") || role.includes("RH") || role.includes("ADMIN");
    }, [user]);

    useEffect(() => {
        dispatch(fetchCampagnesRequest());
        if (isManagerOrRH) {
            dispatch(fetchManagerInboxRequest());
        } else if (user?.id || user?.uid) {
            dispatch(fetchSkillEvalsByUserRequest(user.id || user.uid));
        }
    }, [dispatch, isManagerOrRH, user]);

    const team = useMemo(() => {
        return (inboxManager || []).map(e => ({
            ...e,
            initiales: e.userFullName ? e.userFullName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : "??",
            nom: e.userFullName,
            color: '#204385'
        }));
    }, [inboxManager]);

    const filteredTeam = team.filter(u => 
        u.nom?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const activeCampagnes = useMemo(() => 
        (campagnesList || []).filter(c => c.status === true), 
    [campagnesList]);

    useEffect(() => {
        if (!selectedCampagneId && activeCampagnes.length > 0) {
            const defaultCamp = activeCampagnes.find(c => c.isDefault);
            if (defaultCamp) setSelectedCampagneId(defaultCamp.id.toString());
        }
    }, [activeCampagnes, selectedCampagneId]);

    const gapData = useMemo(() => {
        let evals = [];
        if (isManagerOrRH) {
            if (!selectedUser) return [];
            evals = [selectedUser]; 
        } else {
            evals = skillEvaluations;
        }

        const found = evals.find(e => !selectedCampagneId || e.campagneId === parseInt(selectedCampagneId));
        if (!found || !found.niveaux) return [];

        return found.niveaux.map(n => ({
            skill: n.skillNom,
            obtenu: n.scoreObtenu || n.scoreManager || n.scoreCollab || 0,
            requis: n.scoreRequis || 3
        }));
    }, [isManagerOrRH, selectedUser, skillEvaluations, selectedCampagneId]);

    return (
        <div className="eval-page">
            <div className="eval-header">
                <div>
                    <h1>Analyse des Écarts</h1>
                    <p>Comparez vos compétences aux requis du poste sous forme de tableau.</p>
                </div>
            </div>

            <div className="eval-campaign-bar">
                <span className="eval-campaign-label">Campagne :</span>
                <select className="eval-select" value={selectedCampagneId} onChange={(e) => setSelectedCampagneId(e.target.value)}>
                    <option value="">-- Sélectionnez une campagne --</option>
                    {activeCampagnes.map(c => (
                        <option key={c.id} value={c.id}>{c.nom} {c.anneeCampagne ? `(${c.anneeCampagne})` : ''}</option>
                    ))}
                </select>
            </div>

            <div className="eval-layout">
                {isManagerOrRH && (
                    <aside className="eval-sidebar">
                        <div className="eval-toolbar">
                            <div className="eval-search-box">
                                <Search className="eval-search-icon" size={16} />
                                <input 
                                    type="text" 
                                    className="eval-input" 
                                    placeholder="Chercher collaborateur..." 
                                    value={searchQuery} 
                                    onChange={(e) => setSearchQuery(e.target.value)} 
                                />
                            </div>
                        </div>
                        <div className="eval-list">
                            {filteredTeam.map(u => (
                                <div key={u.id} className={`eval-user-card ${selectedUser?.id === u.id ? 'active' : ''}`} onClick={() => setSelectedUser(u)}>
                                    <div className="eval-avatar" style={{ backgroundColor: u.color }}>{u.initiales}</div>
                                    <div className="eval-user-info">
                                        <div className="eval-user-name">{u.nom}</div>
                                        <div className="eval-user-sub">{u.campagneNom}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </aside>
                )}

                <main className="eval-main">
                    {isManagerOrRH && !selectedUser ? (
                        <div className="eval-empty">
                            <Users size={64} className="eval-empty-icon" />
                            <h3>Suivi des Écarts</h3>
                            <p>Sélectionnez un membre de votre équipe à gauche pour voir ses scores détaillés.</p>
                        </div>
                    ) : gapData.length > 0 ? (
                        <div className="eval-content">
                            <div className="eval-card">
                                <div className="eval-section-title">
                                    <Info size={18} /> Tableau des écarts - {isManagerOrRH ? selectedUser.nom : user.firstName + ' ' + user.lastName}
                                </div>
                                <SkillGapTable data={gapData} />
                                <div style={{ marginTop: '24px', fontSize: '13px', color: '#64748b' }}>
                                    Ce tableau permet d'identifier rapidement les compétences à renforcer (écart négatif) ou les expertises maîtrisées.
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="eval-empty">
                            <Info size={64} className="eval-empty-icon" />
                            <h3>Aucune donnée</h3>
                            <p>Aucune évaluation validée n'a été trouvée pour cette campagne.</p>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
